import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { supabase } from "@/ajax/clients/supabase";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { setRecruiterUserIdInStorage } from "@/features/recruiter-tracking/recruiterCode";
import { PublicJobDetail } from "@/features/job-post/public-job-detail";
import { PublicJobDetailSkeleton } from "@/features/job-post/public-job-detail-skeleton";
import {
  JobNotAvailableMessage,
  JobUnavailableState,
} from "@/features/job-post/job-unavailable-state";
import { PublicJobCtaCard } from "@/features/job-post/public-job-cta-card";
import { useInView } from "@/hooks/use-in-view";
import { buildJobSignInSearch } from "@/features/job-post/utils/build-job-signin-search";

const LOGO_PATH = "/static/images/logo.png";

export const Route = createFileRoute("/jobs/$id/")({
  validateSearch: z.object({
    ref: z.string().uuid().optional().catch(undefined), // malformed ref must degrade gracefully, not error the page
    l: z.string().optional().catch(undefined),
  }),
  beforeLoad: async ({ params, search }) => {
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      throw redirect({
        to: "/provider/jobs/$id",
        params: { id: params.id },
        search: { ref: search.ref },
      });
    }
  },
  component: PublicJobRoute,
});

function PublicJobRoute() {
  const { id } = Route.useParams();
  const { ref, l } = Route.useSearch();
  const trpc = useTRPC();
  const {
    data: job,
    isLoading,
    error,
    refetch: refetchJob,
  } = useQuery(trpc.jobPost.getPublic.queryOptions({ id }, { retry: false }));

  const {
    ref: ctaCardRef,
    isInView: isCtaCardInView,
    isAbove: isCtaCardAbove,
  } = useInView<HTMLDivElement>();
  const ctaCardReached = isCtaCardInView || isCtaCardAbove;

  useEffect(() => {
    if (ref) {
      // Safety net so attribution survives flows that drop the query
      // string. Unlike the authed page, `ref` stays in the URL here so it
      // can be reused for the sign-up CTA and re-sharing.
      setRecruiterUserIdInStorage(id, ref).catch(console.error);
    }
  }, [id, ref]);

  const signinSearch = buildJobSignInSearch({
    jobId: id,
    recruiterUserId: ref,
    referralCode: l,
  });
  const unavailable = !isLoading && (error || !job);
  const signUpAction = (
    <Button asChild size="lg">
      <Link
        to="/auth-provider/signin"
        search={buildJobSignInSearch({ referralCode: l })}
      >
        Sign up to explore all jobs
      </Link>
    </Button>
  );

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto max-w-4xl px-4 md:px-8 py-4 flex items-center justify-between">
          <Link to="/">
            <img src={LOGO_PATH} alt="Saile Company Logo" className="h-8" />
          </Link>
          <Link
            to="/auth-provider/signin"
            search={signinSearch}
            className="text-sm font-medium text-primary hover:text-primary-dark"
          >
            Sign in
          </Link>
        </div>
      </header>

      <div
        className={cn(
          "container mx-auto max-w-4xl px-4 md:px-8",
          unavailable
            ? // 65px = header height
              "flex-1 flex flex-col justify-center pb-[65px]"
            : "pt-4 pb-32 md:pt-8 md:pb-8 space-y-6",
        )}
      >
        {isLoading && <PublicJobDetailSkeleton />}

        {/* A failed request is not evidence the job closed, so only an
            empty result asks the API why. */}
        {unavailable && error && (
          <JobNotAvailableMessage actions={signUpAction} />
        )}
        {unavailable && !error && (
          <JobUnavailableState
            jobId={id}
            onReopened={refetchJob}
            actions={signUpAction}
          />
        )}

        {!isLoading && job && (
          <>
            <PublicJobDetail job={job} />
            <PublicJobCtaCard
              ref={ctaCardRef}
              title={job.title}
              signinSearch={signinSearch}
            />
          </>
        )}
      </div>

      {!isLoading && job && (
        <div
          className={cn(
            "fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 shadow-[0_-4px_10px_rgba(0,0,0,0.1)] z-20 transition-all duration-300",
            ctaCardReached
              ? "translate-y-full opacity-0 pointer-events-none"
              : "translate-y-0 opacity-100",
          )}
        >
          <div className="safe-area-bottom">
            <div className="container mx-auto max-w-4xl px-4 py-3 md:px-8 md:py-4 flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
              <p className="text-sm sm:text-base text-gray-700 font-medium text-center sm:text-left w-full sm:w-auto sm:flex-1 min-w-0 truncate px-4 sm:px-0">
                {job.title
                  ? `Interested in "${job.title}"?`
                  : "Interested in this position?"}
              </p>
              <Button asChild size="lg" className="w-full sm:w-auto shrink-0">
                <Link to="/auth-provider/signin" search={signinSearch}>
                  Sign up to apply
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
